import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as departmentsApi from './departments.api';

import type {
  CreateDepartmentRequest,
  DepartmentListQuery,
  UpdateDepartmentRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: DepartmentListQuery) => ['departments', 'list', query] as const,
  detail: (id: string) => ['departments', 'detail', id] as const,
};

export const useDepartmentsList = (query: DepartmentListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => departmentsApi.listDepartments(query),
    staleTime: 30_000,
  });

export const useCreateDepartment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDepartmentRequest) => departmentsApi.createDepartment(payload),
    onSuccess: (dept) => {
      qc.invalidateQueries({ queryKey: ['departments', 'list'] });
      toast.success(`Added ${dept.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add department'),
  });
};

export const useUpdateDepartment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDepartmentRequest }) =>
      departmentsApi.updateDepartment(id, payload),
    onSuccess: (dept) => {
      qc.invalidateQueries({ queryKey: ['departments', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(dept.id) });
      toast.success('Department updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update department'),
  });
};

export const useDeleteDepartment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => departmentsApi.deleteDepartment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments', 'list'] });
      toast.success('Department deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete department'),
  });
};

export const useBulkDeleteDepartments = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => departmentsApi.bulkDeleteDepartments(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete departments'),
  });
};

export const useBulkSetDepartmentsStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      departmentsApi.bulkSetDepartmentsStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update department status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
